import { useAppSelector } from "../app/appHooks";
import { useTranslation } from "react-i18next";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";
import React from "react";
import { Button, Paper } from "@material-ui/core";
import { RFIDPolicy } from "../../helpers/constants";
import { updateRFIDPolicy } from "./settingsThunks";
import { useDispatch } from "react-redux";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    rfidSettings: {
      padding: theme.spacing(6, 7),
      width: "550px",
      overflowY: "auto",
    },
    header: {
      marginBottom: 16,
      fontWeight: 600,
      textAlign: "center",
    },
    group: {
      width: "85%",
      "& > label + label": {
        borderTop: "1px solid #ddd",
      },
      marginTop: theme.spacing(2),
    },
    optionBox: {
      display: "flex",
      alignItems: "center",
      padding: "12px 16px",
      border: "none",
      borderRadius: 0,
      marginBottom: 12,
      cursor: "pointer",
      transition: "background 120ms ease, border-color 120ms ease",
      "&:hover": {
        background: theme.palette.action.hover,
      },
    },
    optionContainer: {
      display: "grid",
      rowGap: 4,
      paddingRight: theme.spacing(2),
    },
    title: { fontWeight: 600 },
    desc: { fontSize: 14 },
    radio: {
      marginLeft: "auto",
      WebkitAppearance: "none",
      MozAppearance: "none",
      appearance: "none",
      height: "18px",
      width: "18px",
      padding: 0,
      minWidth: "18px",
      borderRadius: "50%",
      border: `2px solid ${theme.palette.primary.main}`,
      position: "relative",
      cursor: "pointer",
      "&:hover": {
        borderColor: theme.palette.primary.main,
        background: "transparent",
      },
      "&::after": {
        content: '""',
        position: "absolute",
        top: "50%",
        left: "50%",
        width: "10px",
        height: "10px",
        borderRadius: "50%",
        background: theme.palette.primary.main,
        transform: "translate(-50%, -50%) scale(0)",
        transition: "transform 120ms ease",
      },
      "&:checked::after": {
        transform: "translate(-50%, -50%) scale(1)",
      },
      "&:focus-visible": {
        outline: `2px solid ${theme.palette.primary.main}`,
        outlineOffset: 2,
      },
      "&:disabled": {
        cursor: "not-allowed",
        opacity: 0.5,
        borderColor: theme.palette.action.disabled,
      },
    },
    buttonContainer: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      marginTop: theme.spacing(6),
    },
    rfidFormContainer: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
    },
    buttonRoot: {
      fontFamily: theme.typography.secondaryFontFamily,
      fontSize: "1.1rem !important",
      minWidth: "120px",
    },
  })
);

export const RFIDSettingsForm = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const community = useAppSelector(
    (state) => state.headerState.selectedCommunity
  );

  const [selected, setSelected] = React.useState<string | undefined>(
    community?.rfid_policy || RFIDPolicy.DISABLED
  );

  const handleSave = () => {
    if (community && selected) {
      dispatch(
        updateRFIDPolicy({
          community_id: community.id,
          rfid_policy: selected,
        })
      );
    }

    return;
  };

  return (
    <Paper className={classes.rfidSettings} elevation={3} square>
      <div className={classes.rfidFormContainer}>
        <h2 id="rfid-settings-title" className={classes.header}>
          {t("rfid_policy_title")}
        </h2>

        <div
          role="radiogroup"
          aria-labelledby="rfid-settings-title"
          className={classes.group}
        >
          <label className={classes.optionBox}>
            <div className={classes.optionContainer}>
              <div className={classes.title}>
                {t("rfid_policy_block_title")}
              </div>
              <div className={classes.desc}>{t("rfid_policy_block_desc")}</div>
            </div>
            <input
              type="radio"
              name="rfidPolicy"
              value={RFIDPolicy.BLOCK}
              checked={selected === RFIDPolicy.BLOCK}
              onChange={() => setSelected(RFIDPolicy.BLOCK)}
              aria-label={t("rfid_policy_block_title")}
              className={classes.radio}
            />
          </label>

          <label className={classes.optionBox}>
            <div className={classes.optionContainer}>
              <div className={classes.title}>{t("rfid_policy_warn_title")}</div>
              <div className={classes.desc}>{t("rfid_policy_warn_desc")}</div>
            </div>
            <input
              type="radio"
              name="rfidPolicy"
              value={RFIDPolicy.WARN}
              checked={selected === RFIDPolicy.WARN}
              onChange={() => setSelected(RFIDPolicy.WARN)}
              aria-label={t("rfid_policy_warn_title")}
              className={classes.radio}
            />
          </label>

          <label className={classes.optionBox}>
            <div className={classes.optionContainer}>
              <div className={classes.title}>
                {t("rfid_policy_disabled_title")}
              </div>
              <div className={classes.desc}>
                {t("rfid_policy_disabled_desc")}
              </div>
            </div>
            <input
              type="radio"
              name="rfidPolicy"
              value={RFIDPolicy.DISABLED}
              checked={selected === RFIDPolicy.DISABLED}
              onChange={() => setSelected(RFIDPolicy.DISABLED)}
              aria-label={t("rfid_policy_disabled_title")}
              className={classes.radio}
            />
          </label>
        </div>

        <div className={classes.buttonContainer}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            className={classes.buttonRoot}
            size="small"
            onClick={() => {
              handleSave();
            }}
          >
            {t("save")}
          </Button>
        </div>
      </div>
    </Paper>
  );
};
